import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Video, Info } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface VideoStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const VideoStep = ({ onNext, onBack, initialData }: VideoStepProps) => {
	const [videoUrl, setVideoUrl] = useState(initialData?.videoUrl || "");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!videoUrl.trim()) {
			alert("Please enter a video URL");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "video",
						data: {
							video: {
								intro_video_url: videoUrl,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save video");
			}

			onNext({ videoUrl });
		} catch (error) {
			console.error("Error saving video:", error);
			alert("Failed to save video. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 10 of 15</span>
					<span className={styles.progressLabel}>Introduction Video</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "70%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Video className={styles.cardIcon} />
						Introduction Video
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Share a personal video introduction with potential clients to help
						them get to know you.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Video Guidelines */}
						<div className={styles.infoAlert}>
							<div className="flex items-start space-x-3">
								<Info className={styles.infoIcon} />
								<div className={styles.infoText}>
									<h4 className="font-medium mb-2">Video Guidelines:</h4>
									<ul className="space-y-1 list-disc list-inside">
										<li>Introduce yourself and your experience</li>
										<li>Share your approach to doula care</li>
										<li>Speak directly to expecting parents</li>
										<li>Use good lighting and clear audio</li>
									</ul>
								</div>
							</div>
						</div>

						{/* Video Upload Section */}
						<div className="space-y-4">
							<div className="text-center">
								<Video className="w-16 h-16 text-newTheme-spruce mx-auto mb-4 opacity-60" />
								<h3 className="text-lg font-medium mb-2 text-newTheme-spruce font-karla">
									Upload Your Video
								</h3>
								<p className={`${styles.cardSubtitle} mb-4`}>
									Upload your video to YouTube, Vimeo, Loom, Veed, or another
									platform and paste the link below.
								</p>
							</div>

							<div>
								<label className={`block mb-2 ${styles.fieldLabel}`}>
									Video URL
								</label>
								<Input
									type="url"
									placeholder="https://youtube.com/watch?v=..."
									value={videoUrl}
									onChange={(e) => setVideoUrl(e.target.value)}
									className="font-karla"
									required
								/>
								<p className={styles.helperText}>
									Supported platforms: YouTube, Vimeo, Loom, Veed, Google Drive
									(public), Dropbox (public), or any public video link
								</p>
							</div>

							{videoUrl && (
								<motion.div
									className="bg-green-50 border border-green-200 rounded-lg p-3"
									initial={{ opacity: 0, scale: 0.95 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ duration: 0.3 }}
								>
									<p className="text-sm text-green-800 font-karla">
										✓ Video URL added. Parents will be able to watch your
										introduction video.
									</p>
								</motion.div>
							)}
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
